
import styled from "styled-components";
import { ViewContext } from "../Contexts";
import { TifynFooter } from "@tiffin/components";
import { customerOrdersReducer } from "@tiffin/app-common";
import { MenuBar } from "../components/MenuBar";
import { CustomerOrderList } from "../components/orders/CustomerOrderList";
import TifynLogo from "../assets/images/tifyn-logo.png"
import { NavBar } from "../components/NavBar";
import { useAppDispatch, useAppSelector } from "../hooks";

import { Unsubscribe } from "firebase/firestore";
import { IonIcon } from "@ionic/react";
import { receiptOutline } from "ionicons/icons"
import { useContext, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "antd";

export const Orders = styled(BaseOrders)`
  display: grid;
  grid-template-columns: auto 1fr;
  height: 100vh;

  @media (max-width:768px) {
    grid-template-columns: 100%;
  }

  .header {
    background: #fff;
    padding: 20px;
    font-size: var(--font-size-medium);
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 5px;

    ion-icon {
      font-size: var(--font-size-xlarge);
    }
  }

  .page {
    display: grid;
    grid-template-rows: auto 1fr auto;
    height: calc(var(--vh, 1vh) * 100);

    @media (max-width:768px) {
      padding-bottom: 70px;
    }
    
    .container {
      padding: 10px;
      overflow: auto;
    }

    .unauthenticated {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 20px;
      color: var(--ion-color-medium);
      font-weight: 600;
      margin: 10px;
      background: #fff;
      text-align: center;
      padding: 20px;
      border-radius: 8px;

      .unauthenticated-btns {
        display: flex;
        gap: 10px;
      }

      svg {
        width: 100px;
        height: fit-content;
      }
    }

    ion-item {
      border-radius: 8px;
      box-shadow: rgba(128, 128, 128, 0.08) -2px 2px 2px, rgba(128, 128, 128, 0.04) 0px 2px 2px;

      &::part(native) {
        border-radius: 8px;
      }
    }
  }
`

interface IOrders {
  className?: string
}

function BaseOrders({className}: IOrders) {

  const status = useAppSelector((state) => state.customerOrders.status)
  const partner = useAppSelector((state) => state.store)
  const orders = useAppSelector((state) => state.customerOrders.orders)
  const hasMoreOrders = useAppSelector((state) => state.customerOrders.hasMoreOrders)
  const authenticateStatus = useAppSelector((state) => state.authenticate.status)

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams();

  const view = useContext(ViewContext);

  //Unsubscribe functions must be stored here as they cannot be stored in redux since they are not serializable
  const subscribedOrders = useRef<Map<string, Unsubscribe>>(new Map())

  const callbacksRef = useRef({
    addSubscription: (id: string, unsubcribe: Unsubscribe) => {
      if(subscribedOrders.current.has(id)) {
        //Unsubscribe to old subscription as new subscription was made
        subscribedOrders.current.get(id)!()
      }
      subscribedOrders.current.set(id, unsubcribe)
    },
    removeSubscription: (id: string) => {
      if(subscribedOrders.current.has(id)) {
        //Unsubscribe
        subscribedOrders.current.get(id)!()
      }
    }
  })

  useEffect(() => {
    if(!partner.details?.id) return;

    dispatch(customerOrdersReducer.getOrders({...callbacksRef.current, partnerId: partner.details?.id}))

    return () => {
      //Unsubscribe from all subscribed orders
      subscribedOrders.current.forEach((value) => {
        value()
      })
    }
  }, [dispatch, partner.details?.id])

  function refreshPage() {
    dispatch(customerOrdersReducer.getOrders({...callbacksRef.current, partnerId: partner.details?.id}))
  }

  return (
    <div className={className}>
      {
        view === "web" && <NavBar />
      }
      <div className="page" style={{paddingBottom: view === "mobile" ? 70 : 0}}>
        {
          partner.details &&
          <>
            <div className="header">
              <IonIcon icon={receiptOutline} />
              <span>Orders</span>
            </div>
            {
              authenticateStatus === "authenticated" &&
              <div className="container">
                <CustomerOrderList
                  orders={orders}
                  loading={status === "updating"}
                  hasMore={hasMoreOrders}
                  fetchMore={() => dispatch(customerOrdersReducer.getNextOrders({partnerId: partner.details?.id}))}
                  refresh={refreshPage}
                  hideViewStoreBtn={true}
                  messageCallback={() => {
                    navigate({pathname: `/${partner.details?.urlName}/messages`, search: searchParams.toString()})
                  }}
                />
              </div>
            }
            {
              authenticateStatus === "unauthenticated" &&
              <div className="unauthenticated">
                <span>You must create an account or log in to see past orders</span>
                <div className="unauthenticated-btns">
                  <Button onClick={() => navigate({pathname: `/${partner.details?.urlName}/signin`, search: searchParams.toString()})}>Log In</Button>
                  <Button onClick={() => navigate({pathname: `/${partner.details?.urlName}/signin`, search: searchParams.toString()})} type="primary">Sign Up</Button>
                </div>
              </div>
            }
            {
              view === "web" &&
              <TifynFooter
                logoAlt="Tifyn Logo"
                logoSrc={TifynLogo}
              />
            }
          </>
        }
      </div>
      {
        view === "mobile" && <MenuBar />
      }
    </div>
  )
}